import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Flex } from 'design-system-web';
import { H5, S, P } from 'components/typography';
import { Chip } from 'components/ads';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import {
  getWatchlistSeverityTranslation,
  getWatchlistSeverityChipStyle,
} from '../../helpers/watchlistSeverityHelpers';
import { useAttachmentByEntityData } from 'hooks/useAttachmentByEntityData';

import styles from './WatchlistMatchCards.scss';

const TRANSLATION_KEYS = {
  matchesDetected: 'web.admin.channel.visitor.log.watchlistMatchCards.matchesDetected',
  viewAllMatches: 'web.admin.channel.visitor.log.watchlistMatchCards.viewAllMatches',
  firstName: 'web.admin.channel.visitor.log.watchlistMatchCards.firstName',
  lastName: 'web.admin.channel.visitor.log.watchlistMatchCards.lastName',
  email: 'web.admin.channel.visitor.log.watchlistMatchCards.email',
  phoneNumber: 'web.admin.channel.visitor.log.watchlistMatchCards.phoneNumber',
  reportedBy: 'web.admin.channel.visitor.log.watchlistMatchCards.reportedBy',
  notesToStaff: 'web.admin.channel.visitor.log.watchlistMatchCards.notesToStaff',
};

const ELLIPSIS = '\u2026'; // Unicode ellipsis character '...'

interface VisitorMatchingField {
  primaryValue: string;
  alternateValues: string[];
  matchType: string;
}

export interface WatchlistMatch {
  id: string;
  organizationId: string;
  visitorMatchingInfo: {
    firstName: VisitorMatchingField | null;
    lastName: VisitorMatchingField | null;
    email: VisitorMatchingField | null;
    phone: VisitorMatchingField | null;
  };
  severityLevel: string;
  notes: string;
  attachmentIds: string[];
  reportedBy?: string;
}

interface WatchlistMatchCardsProps {
  watchlists: WatchlistMatch[];
  onViewAllMatches?: () => void;
}

export function WatchlistMatchCards({
  watchlists,
  onViewAllMatches,
}: WatchlistMatchCardsProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);

  const totalMatches = watchlists.length;
  const currentWatchlist = watchlists[currentPage];

  if (!currentWatchlist) {
    return null;
  }

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalMatches - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const formatFieldValue = (field: VisitorMatchingField | null): string => {
    if (!field?.primaryValue) return '-';

    const alternates = field.alternateValues?.filter(Boolean) || [];

    if (alternates.length === 0) {
      return field.primaryValue;
    }

    return `${field.primaryValue} (${alternates.join(', ')})`;
  };

  return (
    <Flex direction="column" gap={6} className={styles.watchlistMatchCards}>
      <Flex direction="column" gap={5}>
        <Flex align="center" gap={4}>
          <Icon
            name="exclamation-triangle"
            set={ICON_SET_FONTAWESOME}
            className={styles.warningIcon}
            size="medium"
          />
          <H5 className={styles.headerTitle}>
            {t(TRANSLATION_KEYS.matchesDetected, { count: totalMatches })}
          </H5>
        </Flex>

        {onViewAllMatches && (
          <button
            type="button"
            onClick={onViewAllMatches}
            className={styles.viewAllLink}
          >
            <Icon
              name="external-link-square-alt"
              set={ICON_SET_FONTAWESOME}
              size="small"
            />
            {t(TRANSLATION_KEYS.viewAllMatches)}
          </button>
        )}
      </Flex>

      {totalMatches > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalMatches}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onPageSelect={setCurrentPage}
        />
      )}

      <Flex>
        <Chip
          value={getWatchlistSeverityTranslation(
            currentWatchlist.severityLevel,
            t
          )}
          size="xs"
          type={getWatchlistSeverityChipStyle(currentWatchlist.severityLevel)}
        />
      </Flex>

      <Flex direction="column" gap={4}>

        <MatchField
          label={t(TRANSLATION_KEYS.firstName)}
          value={formatFieldValue(
            currentWatchlist.visitorMatchingInfo.firstName
          )}
        />

        <MatchField
          label={t(TRANSLATION_KEYS.lastName)}
          value={formatFieldValue(
            currentWatchlist.visitorMatchingInfo.lastName
          )}
        />

        <MatchField
          label={t(TRANSLATION_KEYS.email)}
          value={formatFieldValue(currentWatchlist.visitorMatchingInfo.email)}
        />

        <MatchField
          label={t(TRANSLATION_KEYS.phoneNumber)}
          value={formatFieldValue(currentWatchlist.visitorMatchingInfo.phone)}
        />

        {currentWatchlist.reportedBy && (
          <MatchField
            label={t(TRANSLATION_KEYS.reportedBy)}
            value={currentWatchlist.reportedBy}
          />
        )}

        <MatchField
          label={t(TRANSLATION_KEYS.notesToStaff)}
          value={currentWatchlist.notes || '-'}
        />

        <WatchlistAttachments watchlistId={currentWatchlist.id} />
      </Flex>
    </Flex>
  );
}

interface MatchFieldProps {
  label: string;
  value: string;
}

function MatchField({ label, value }: MatchFieldProps) {
  return (
    <Flex direction="column" gap={1} className={styles.matchField}>
      <S className={styles.fieldLabel}>{label}</S>
      <P className={styles.fieldValue}>{value}</P>
    </Flex>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  onPageSelect: (page: number) => void;
}

function Pagination({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  onPageSelect,
}: PaginationProps) {
  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];
    const maxVisiblePages = 3;

    // Calculate the sliding window start position
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(startPage + maxVisiblePages, totalPages);

    // Adjust start if we're near the end
    if (endPage - startPage < maxVisiblePages) {
      startPage = Math.max(0, endPage - maxVisiblePages);
    }

    // Show ellipsis at the start if not showing first page
    if (startPage > 0) {
      pages.push(
        <span key="ellipsis-start" className={styles.ellipsis}>
          {ELLIPSIS}
        </span>
      );
    }

    for (let i = startPage; i < endPage; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          className={`${styles.pageButton} ${currentPage === i ? styles.active : ''}`}
          onClick={() => onPageSelect(i)}
        >
          {i + 1}
        </button>
      );
    }

    // Show ellipsis at the end if not showing last page
    if (endPage < totalPages) {
      pages.push(
        <span key="ellipsis-end" className={styles.ellipsis}>
          {ELLIPSIS}
        </span>
      );
    }

    return pages;
  };

  return (
    <Flex align="center" justify="center" gap={2}>
      <button
        type="button"
        className={styles.navButton}
        onClick={onPrevious}
        disabled={currentPage === 0}
        aria-label="Previous"
      >
        <Icon name="chevron-left" set={ICON_SET_FONTAWESOME} size="medium" />
      </button>

      {renderPageNumbers()}

      <button
        type="button"
        className={styles.navButton}
        onClick={onNext}
        disabled={currentPage === totalPages - 1}
        aria-label="Next"
      >
        <Icon name="chevron-right" set={ICON_SET_FONTAWESOME} size="medium" />
      </button>
    </Flex>
  );
}

function WatchlistAttachments({ watchlistId }: { watchlistId: string }) {
  const { attachments, loading } = useAttachmentByEntityData({
    entityId: watchlistId,
    editMode: false,
  });

  const attachmentsWithThumbnails = attachments?.filter(
    (attachment) => attachment.thumbnailSignedUrl
  );

  if (loading || !attachmentsWithThumbnails || attachmentsWithThumbnails.length === 0) {
    return null;
  }

  return (
    <div className={styles.attachmentsGrid}>
      {attachmentsWithThumbnails.map((attachment) => (
        <div key={attachment.id} className={styles.attachmentThumbnail}>
          <img src={attachment.thumbnailSignedUrl ?? undefined} alt="Watchlist attachment" />
        </div>
      ))}
    </div>
  );
}

